import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, ShieldCheck, AlertTriangle, RefreshCw, X, Send, Plus, Loader2, Info,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * EmailSettingsTab — Settings → Email
 *
 * Lets companies configure the From / Reply-To address that customer-facing
 * emails (invoices, estimates, appointment confirmations, portal invites,
 * receipts) are sent from. Backed by MailerSend Sender Identities —
 * MailerSend handles the SPF/DKIM/DMARC signing on the Fieldz AI side, and
 * the user only has to confirm ownership by clicking a link in their inbox.
 *
 * Multi-location: the master identity applies to every location by
 * default. Users can optionally add a per-location identity that takes
 * priority for invoices/estimates/jobs scoped to that location.
 */
export default function EmailSettingsTab() {
  const [identities, setIdentities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null); // null = master, "<id>" = location override
  const [testOpen, setTestOpen] = useState(false);
  const [testingFor, setTestingFor] = useState(null);
  const [invitationPref, setInvitationPref] = useState("auto");
  const [savingPref, setSavingPref] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [idRes, locRes, prefRes] = await Promise.all([
        axios.get(`${API}/sender-identities/all`, getAuth()),
        axios.get(`${API}/store/locations`, getAuth()).catch(() => ({ data: { locations: [] } })),
        axios.get(`${API}/sender-identities/preferences`, getAuth()).catch(() => ({ data: {} })),
      ]);
      const ids = idRes.data?.identities || [];
      setIdentities(ids);
      setLocations(locRes.data?.locations || (Array.isArray(locRes.data) ? locRes.data : []));
      const pref = prefRes.data?.invitation_identity_id;
      if (!pref) setInvitationPref("auto");
      else if (pref === "platform") setInvitationPref("platform");
      else if (ids.some(i => i.identity_id === pref)) setInvitationPref(pref);
      else setInvitationPref("auto");
    } catch (err) {
      console.error("Email settings load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const masterIdentity = identities.find(i => !i.location_id) || null;
  const perLocation = identities.filter(i => !!i.location_id);
  const verifiedIdentities = identities.filter(i => i.status === "verified");

  const saveInvitationPref = async (value) => {
    const prev = invitationPref;
    setInvitationPref(value);
    setSavingPref(true);
    try {
      await axios.put(`${API}/sender-identities/preferences`, {
        invitation_identity_id: value === "auto" ? null : value,
      }, getAuth());
      toast.success("Invitation sender updated");
    } catch (err) {
      setInvitationPref(prev);
      toast.error(err.response?.data?.detail || "Failed to save invitation sender");
    } finally {
      setSavingPref(false);
    }
  };

  const identityLabel = (i) => {
    const scope = i.location_id
      ? (locations.find(l => l.location_id === i.location_id)?.name || "Location")
      : "Default sender";
    return `${i.sender_email} — ${scope}`;
  };

  const handleConnect = async (payload) => {
    try {
      await axios.post(`${API}/sender-identities`, payload, getAuth());
      toast.success("Verification email sent. Check your inbox to confirm.");
      setConnectOpen(false);
      setEditingLocation(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect sender");
    }
  };

  const refreshOne = async (identity) => {
    try {
      const q = identity.location_id ? `?location_id=${encodeURIComponent(identity.location_id)}` : "";
      const res = await axios.post(`${API}/sender-identities/refresh${q}`, {}, getAuth());
      const refreshed = res.data?.identity;
      if (refreshed?.status === "verified") toast.success("Verified!");
      else toast.message("Still waiting on verification. Check your inbox.");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    }
  };

  const disconnectOne = async (identity) => {
    if (!window.confirm(`Disconnect ${identity.sender_email}?`)) return;
    try {
      const q = identity.location_id ? `?location_id=${encodeURIComponent(identity.location_id)}` : "";
      await axios.delete(`${API}/sender-identities${q}`, getAuth());
      toast.success("Disconnected");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disconnect");
    }
  };

  const assignExisting = async (locationId, identityId) => {
    try {
      const res = await axios.post(`${API}/sender-identities/assign`, {
        location_id: locationId, identity_id: identityId,
      }, getAuth());
      toast.success(res.data?.message || "Sender assigned to location");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign sender");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-tab">
      <div>
        <h3 className="text-lg sm:text-xl font-bold text-foreground">Email Settings</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Send customer emails (invoices, estimates, appointment confirmations) from your own
          email address instead of Fieldz AI. We'll send through MailerSend's verified
          infrastructure — your DKIM, SPF, and DMARC are all signed automatically.
        </p>
      </div>

      {/* Fallback warning when nothing's connected */}
      {!masterIdentity && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="pt-5 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-foreground">Currently sending as Fieldz AI</div>
              <p className="text-sm text-muted-foreground mt-1">
                Until you connect your own email address, customer-facing emails will be sent
                from <code className="text-foreground bg-muted px-1.5 py-0.5 rounded">info@fieldzai.com</code>.
                Connect your domain below for stronger branding and better inbox delivery.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MASTER (account-wide) identity */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Default sender</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Used for every customer-facing email unless a location-specific sender is set below.
            </p>
          </div>
          {!masterIdentity && (
            <Button
              size="sm"
              onClick={() => { setEditingLocation(null); setConnectOpen(true); }}
              data-testid="connect-master-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Connect
            </Button>
          )}
        </CardHeader>
        {masterIdentity && (
          <CardContent>
            <SenderRow
              identity={masterIdentity}
              onRefresh={() => refreshOne(masterIdentity)}
              onDisconnect={() => disconnectOne(masterIdentity)}
              onTest={() => { setTestingFor(masterIdentity); setTestOpen(true); }}
            />
          </CardContent>
        )}
      </Card>

      {/* USER INVITATION EMAILS — which sender delivers team/technician invites */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">User invitation emails</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Choose which sender delivers account-setup invitations to new team members and
            technicians. Invitations always send — if the selected sender isn't verified,
            we fall back to Fieldz AI so new employees can always onboard.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Select value={invitationPref} onValueChange={saveInvitationPref} disabled={savingPref}>
              <SelectTrigger className="w-full sm:w-[420px]" data-testid="invitation-sender-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" data-testid="invitation-sender-auto">
                  Automatic — default sender when verified, else Fieldz AI
                </SelectItem>
                <SelectItem value="platform" data-testid="invitation-sender-platform">
                  Fieldz AI (info@fieldzai.com)
                </SelectItem>
                {verifiedIdentities.map(i => (
                  <SelectItem key={i.identity_id} value={i.identity_id} data-testid={`invitation-sender-${i.identity_id}`}>
                    {identityLabel(i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {savingPref && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />}
          </div>
          {verifiedIdentities.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Verify a sender above to make it selectable here.
            </p>
          )}
        </CardContent>
      </Card>

      {/* PER-LOCATION overrides — only render the section if account has ≥2 locations */}
      {locations.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Per-location senders (optional)</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Override the default sender for emails tied to a specific location.
              Useful when each location has its own brand or email domain.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {locations.map(loc => {
                const override = perLocation.find(i => i.location_id === loc.location_id);
                return (
                  <div key={loc.location_id} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-sm font-medium text-foreground">
                        {loc.name}
                        {loc.industry && (
                          <span className="text-xs text-muted-foreground ml-2">· {loc.industry}</span>
                        )}
                      </div>
                      {!override && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingLocation(loc.location_id); setConnectOpen(true); }}
                          data-testid={`connect-loc-${loc.location_id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add sender
                        </Button>
                      )}
                    </div>
                    {override ? (
                      <SenderRow
                        identity={override}
                        onRefresh={() => refreshOne(override)}
                        onDisconnect={() => disconnectOne(override)}
                        onTest={() => { setTestingFor(override); setTestOpen(true); }}
                      />
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-amber-500">
                          No sender assigned — invoice &amp; estimate emails for this location are blocked until one is set.
                        </p>
                        {verifiedIdentities.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Select onValueChange={(v) => assignExisting(loc.location_id, v)}>
                              <SelectTrigger className="h-8 text-xs w-full sm:w-80" data-testid={`assign-existing-${loc.location_id}`}>
                                <SelectValue placeholder="Assign an existing verified sender…" />
                              </SelectTrigger>
                              <SelectContent>
                                {verifiedIdentities
                                  .filter((i, idx, arr) => arr.findIndex((x) => x.sender_email === i.sender_email) === idx)
                                  .map((i) => (
                                    <SelectItem key={i.identity_id} value={i.identity_id}>
                                      {i.sender_email}{!i.location_id ? " (default)" : ""}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <ConnectDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onConfirm={handleConnect}
        locationId={editingLocation}
        locationName={editingLocation ? (locations.find(l => l.location_id === editingLocation)?.name || "") : ""}
      />
      <TestEmailDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        identity={testingFor}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function statusBadge(status) {
  if (status === "verified") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20">
        <ShieldCheck className="w-3 h-3 mr-1" /> Verified
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge variant="outline" className="text-amber-500 border-amber-500/40">
        Pending verification
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-red-500 border-red-500/40">
      Failed
    </Badge>
  );
}

function SenderRow({ identity, onRefresh, onDisconnect, onTest }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap" data-testid={`sender-row-${identity.identity_id}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Mail className="w-5 h-5 text-muted-foreground shrink-0 mt-1" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-foreground break-all">{identity.sender_name}</div>
            {statusBadge(identity.status)}
          </div>
          <div className="text-sm text-muted-foreground break-all">{identity.sender_email}</div>
          {identity.reply_to_email && identity.reply_to_email !== identity.sender_email && (
            <div className="text-xs text-muted-foreground mt-1">
              Reply-To: <span className="text-foreground">{identity.reply_to_email}</span>
            </div>
          )}
          {identity.status === "pending" && (
            <div className="text-xs text-amber-500 mt-2 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Check {identity.sender_email} for a confirmation email from MailerSend. Once you click
              the verify link, hit Refresh below.
            </div>
          )}
          {identity.status === "failed" && identity.last_error && (
            <div className="text-xs text-red-500 mt-2">{identity.last_error}</div>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onRefresh} data-testid="refresh-sender-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        {identity.status === "verified" && (
          <Button size="sm" variant="outline" onClick={onTest} data-testid="test-sender-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Test
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDisconnect} data-testid="disconnect-sender-btn">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function ConnectDialog({ open, onOpenChange, onConfirm, locationId, locationName }) {
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setSenderName(""); setSenderEmail(""); setReplyTo(""); }
  }, [open]);

  const submit = async () => {
    if (!senderName.trim()) { toast.error("Sender name is required"); return; }
    if (!senderEmail.trim()) { toast.error("Sender email is required"); return; }
    setBusy(true);
    try {
      await onConfirm({
        sender_name: senderName.trim(),
        sender_email: senderEmail.trim().toLowerCase(),
        reply_to_email: replyTo.trim() || undefined,
        location_id: locationId || undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="connect-sender-dialog">
        <DialogHeader>
          <DialogTitle>
            {locationId ? `Add sender for ${locationName}` : "Connect your email"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">From Name</Label>
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Best Boy Locksmith"
              className="mt-1"
              data-testid="connect-sender-name"
            />
          </div>
          <div>
            <Label className="text-xs">From Email</Label>
            <Input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="info@bestboylocksmith.com"
              className="mt-1"
              data-testid="connect-sender-email"
            />
            <p className="text-xs text-muted-foreground mt-1">
              You'll receive a verification email at this address. Click the link to confirm.
            </p>
          </div>
          <div>
            <Label className="text-xs">Reply-To Email (optional)</Label>
            <Input
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="support@bestboylocksmith.com"
              className="mt-1"
              data-testid="connect-sender-replyto"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Where customer replies should land. Defaults to From email if blank.
            </p>
          </div>
          <Card className="bg-muted/30 border-border/50">
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  Emails are sent through MailerSend's verified infrastructure with proper
                  SPF, DKIM, and DMARC signing. You only need to confirm ownership of
                  your email address — no DNS changes required.
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-connect-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="confirm-connect-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Send verification email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function TestEmailDialog({ open, onOpenChange, identity }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setTo(""); }, [open]);

  const submit = async () => {
    if (!to.trim()) { toast.error("Recipient required"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/sender-identities/test`, {
        to: to.trim(),
        location_id: identity?.location_id || undefined,
      }, getAuth());
      if (res.data?.success) {
        toast.success(`Test sent — check ${to} (sending as ${res.data.sending_as})`);
        onOpenChange(false);
      } else {
        toast.error(res.data?.error || "Test failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="test-email-dialog">
        <DialogHeader>
          <DialogTitle>Send test email</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Send a sample email to verify your customers see the right From address and
            the email actually lands in their inbox (not spam).
          </p>
          <div>
            <Label className="text-xs">Recipient</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@example.com"
              className="mt-1"
              data-testid="test-email-to"
            />
          </div>
          {identity?.sender_email && (
            <p className="text-xs text-muted-foreground">
              Sending as: <span className="text-foreground">{identity.sender_email}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="confirm-test-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
